import { TabLockedError, type AppPlatform, type AppStation } from '@ims/app'
import { openLocalStore } from '@ims/local'
import {
  attachBrowserTriggers,
  createWorkerDriver,
  databaseFileName,
  deleteDatabase,
  requestPersistentStorage,
  tabLockName,
  takeOverTabLock,
  tryAcquireTabLock,
} from '@ims/local/browser'
import { SupabaseTransport, createSupabase } from '@ims/remote'
import { browserInstall } from './install.ts'
import { browserUpdates } from './updates.ts'
import type { SpreadsheetRequest, SpreadsheetResponse } from './spreadsheet.worker.ts'
import { browserAlerts } from './alerts.ts'
import { browserPush } from './push.ts'
import { browserScanner } from './scanner.ts'

const INSTALLATION_KEY = 'ims.installation'

/** A stable id for this browser install; billing counters are bound to it. Survives logout. */
function installationId(): string {
  let id = localStorage.getItem(INSTALLATION_KEY)
  if (!id) {
    id = `web-${crypto.randomUUID()}`
    localStorage.setItem(INSTALLATION_KEY, id)
  }
  return id
}

/**
 * On a page reload the previous document releases its lock (and its SQLite
 * worker) asynchronously, so retry briefly before deciding another tab has it.
 */
async function acquireWithRetry(name: string, options: Parameters<typeof tryAcquireTabLock>[1]) {
  for (let attempt = 0; attempt < 8; attempt++) {
    const lock = await tryAcquireTabLock(name, options)
    if (lock) return lock
    await new Promise((r) => setTimeout(r, 200))
  }
  return null
}

function sqliteWorker(): Worker {
  return new Worker(new URL('./sqlite.worker.ts', import.meta.url), {
    type: 'module',
    name: 'sqlite',
  })
}

type RequestBody =
  | { kind: 'read'; data: ArrayBuffer }
  | { kind: 'write'; sheets: { name: string; rows: (string | number)[][] }[] }

/** One short-lived worker per call: nothing stays running after an import. */
function spreadsheetCall(body: RequestBody, transfer: Transferable[] = []) {
  return new Promise<SpreadsheetResponse & { ok: true }>((resolve, reject) => {
    const worker = new Worker(new URL('./spreadsheet.worker.ts', import.meta.url), {
      type: 'module',
      name: 'spreadsheet',
    })
    worker.onmessage = (event: MessageEvent<SpreadsheetResponse>) => {
      worker.terminate()
      const res = event.data
      if (res.ok) resolve(res)
      else reject(new Error(res.error))
    }
    worker.onerror = (event) => {
      worker.terminate()
      reject(new Error(event.message || 'Could not read the file'))
    }
    worker.postMessage({ id: 1, ...body } as SpreadsheetRequest, transfer)
  })
}

const browserStation: AppStation = {
  enterFullscreen: () => void document.documentElement.requestFullscreen?.().catch(() => {}),
  isFullscreen: () => document.fullscreenElement !== null,
  keepAwake: () => {
    let lock: WakeLockSentinel | null = null
    let done = false
    const take = () => {
      if (done || document.visibilityState !== 'visible') return
      void navigator.wakeLock
        ?.request('screen')
        .then((l) => {
          if (done) void l.release()
          else lock = l
        })
        .catch(() => {})
    }
    take()
    // The browser lets the lock go when the page is hidden.
    document.addEventListener('visibilitychange', take)
    return () => {
      done = true
      document.removeEventListener('visibilitychange', take)
      void lock?.release()
    }
  },
}

export function createBrowserPlatform(config: {
  supabaseUrl: string
  supabaseKey: string
  apiUrl: string
  menuUrl: string
}): AppPlatform {
  const supabase = createSupabase(config.supabaseUrl, config.supabaseKey)
  const transport = new SupabaseTransport(supabase)
  const installation = installationId()

  return {
    supabase,
    apiUrl: config.apiUrl,
    menuUrl: config.menuUrl,
    installationId: installation,

    spreadsheets: {
      async read(data) {
        const res = await spreadsheetCall({ kind: 'read', data }, [data])
        return res.sheets ?? []
      },
      async write(sheets) {
        const res = await spreadsheetCall({ kind: 'write', sheets })
        if (!res.blob) throw new Error('Could not create the file')
        return res.blob
      },
    },

    async openStore(userId, tenantId, options) {
      const name = tabLockName(userId, tenantId)
      const lockOptions = { onTakeoverRequested: () => options.onTakeoverRequested() }
      const lock = options.takeOver
        ? await takeOverTabLock(name, lockOptions)
        : await acquireWithRetry(name, lockOptions)
      if (!lock) throw new TabLockedError()
      let driver: Awaited<ReturnType<typeof createWorkerDriver>> | null = null
      try {
        driver = await createWorkerDriver(sqliteWorker(), databaseFileName(userId, tenantId))
        const store = await openLocalStore({
          driver,
          transport,
          tenantId,
          installationId: installation,
        })
        let released = false
        return {
          store,
          release: async () => {
            if (released) return
            released = true
            try {
              await store.close()
            } finally {
              lock.release()
            }
          },
        }
      } catch (error) {
        // Close a database that opened but whose store did not, so its worker lets go.
        await driver?.close().catch(() => {})
        lock.release()
        throw error
      }
    },

    deleteLocalData: (userId, tenantId) =>
      deleteDatabase(sqliteWorker(), databaseFileName(userId, tenantId)),
    attachSyncTriggers: (trigger) => attachBrowserTriggers({ trigger }),
    requestPersistentStorage,
    install: browserInstall,
    updates: browserUpdates,
    reload: () => window.location.reload(),
    alerts: browserAlerts,
    push: browserPush(import.meta.env.VITE_VAPID_PUBLIC_KEY),
    scanner: browserScanner,
    haptics: { tap: () => void navigator.vibrate?.(12) },
    station: browserStation,
  }
}
